// packages import
import { FC, useContext } from 'react'
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Checkbox,
  IconButton
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import { format } from 'date-fns'

// component import
import { CustomPagination } from '../../common/CustomPagination'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { BillsMenu } from '../../menus/Bills'
import { Alert } from '../../common/Alert'
import { printBillV2 } from '../../../../services'

// others
import { BillStatus, UserType } from '../../../../generated'
import { FinalBillsListingProps } from '../../../interfaceTypes'
import { capitalizeCase, formatCurrency, hasAdminAccess, removeUnderScore } from '../../../utils'
import { formatStatus, isContingencyCase, isHourlyCase, statusColors } from '../../../utils/billing'
import { Link } from 'react-router-dom'
import { AuthContext } from '../../../context'
import palette from '../../../theme/palette'

const ColumnHeader: FC<{ label: string; info: string }> = ({ label, info }) => (
  <Box display="inline-flex" alignItems="center" gap={0.5}>
    {label}
    <Tooltip title={info} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  </Box>
)

export const FinalBillsListing: FC<FinalBillsListingProps> = ({
  bills,
  loading,
  refetch,
  selectedFinalBills,
  setSelectedFinalBills
}): JSX.Element => {
  const { isAdmin, isSuperAdmin, isAccountant, currentUser } = useContext(AuthContext)

  const selectedBillIds = selectedFinalBills.selectedIds
  const allSelected = selectedFinalBills.allSelected
  const hasAdminPermissions = hasAdminAccess(currentUser)
  // Trust/credit columns are accounting-sensitive — only accounting/admins see them.
  const trustMode = hasAdminPermissions

  const handleRowPrint = async (billId: number) => {
    if (!Number.isFinite(billId)) return
    try {
      await printBillV2([billId], { markAsDelivered: true })
      // Printing marks the bill as delivered; refetch so "Sent to Client" reflects it.
      refetch?.()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate PDF')
    }
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked

    if (checked) {
      setSelectedFinalBills({ selectedIds: bills?.map(bill => bill.id) || [], allSelected: true })
    } else {
      setSelectedFinalBills({ selectedIds: [], allSelected: false })
    }
  }

  const handleRowSelect = (billId: number) => {
    const isSelected = selectedBillIds.includes(billId)

    if (isSelected) {
      const selectedIds = selectedBillIds.filter(id => id !== billId)
      setSelectedFinalBills({ selectedIds, allSelected: false })
    } else {
      const newSelection = [...selectedBillIds, billId]
      setSelectedFinalBills({ selectedIds: newSelection, allSelected: false })
    }
  }

  const showCheckBox = isAccountant || isAdmin || isSuperAdmin

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} setParams={true} />} height="400px">
      <TableHead>
        <TableRow>
          {showCheckBox && (
            <TableCell width="5%">
              <Checkbox checked={allSelected} onChange={handleSelectAll} size="small" />
            </TableCell>
          )}
          <TableCell width={showCheckBox ? '6%' : '4%'}> ID </TableCell>
          <TableCell width="10%"> Matter # </TableCell>
          <TableCell width="12%"> Attorney </TableCell>
          <TableCell width="10%"> Matter </TableCell>
          <TableCell width="10%"> Matter Type </TableCell>
          <TableCell width="8%"> Generated On </TableCell>
          <TableCell width="10%"> Total </TableCell>
          <TableCell width="10%"> Paid </TableCell>
          <TableCell width="10%"> Written Off </TableCell>
          <TableCell width="10%">
            <ColumnHeader
              label="Balance"
              info="Remaining balance on this bill only (Total − Paid − Written Off)."
            />
          </TableCell>
          {trustMode && (
            <>
              <TableCell width="10%">
                <ColumnHeader
                  label="Outstanding Balance"
                  info="Client's total unpaid balance (remaining principal + interest) on prior final bills for this matter."
                />
              </TableCell>
              <TableCell width="9%">
                <ColumnHeader label="Trust" info="Total trust money (IOLTA + escrow) currently held for this matter." />
              </TableCell>
              <TableCell width="9%">
                <ColumnHeader label="Credits" info="Available unapplied client credits for this matter." />
              </TableCell>
              <TableCell width="10%">
                <ColumnHeader
                  label="Pull from Trust"
                  info="Amount to pull from trust to cover what's owed (Outstanding Balance − Credits)."
                />
              </TableCell>
            </>
          )}
          <TableCell width="6%">
            <ColumnHeader
              label="Sent to Client"
              info="Whether this bill has been sent to the client (via email, or printed & mailed in a batch print run)."
            />
          </TableCell>
          <TableCell width="8%">
            <ColumnHeader label="Sent At" info="Date this bill was last sent to the client." />
          </TableCell>
          <TableCell width="7%"> Status </TableCell>
          <TableCell width="2%"> Action </TableCell>
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={(showCheckBox ? 13 : 12) + (trustMode ? 4 : 0)} />
      ) : (
        <TableBody>
          {bills?.map(bill => {
            const { id, case: caseData, amount, status, issueDate, trustBalance, creditBalance, outstandingBalance, lastSentAt } =
              bill || {}
            const amountPaid = bill.amountPaid || 0
            const balance = amount - amountPaid - (bill.writtenOffPrincipal || 0)
            const writtenOff = bill.writtenOffPrincipal || 0
            const hasTrust = (trustBalance || 0) > 0
            const pullFromTrust = Math.max(0, (outstandingBalance || 0) - (creditBalance || 0))
            const { caseNumber, title, billing, caseToUsers, consultMatterNumber } = caseData || {}

            const responsibleAttorney = caseToUsers?.find(
              caseUser => caseUser?.userType === UserType.ResponsibleAttorney_1
            )
            const attorneyName = responsibleAttorney?.user
              ? `${responsibleAttorney.user.firstName || ''} ${responsibleAttorney.user.lastName || ''}`
              : ''

            const formattedStatus = formatStatus(status)
            const sentColor = lastSentAt ? statusColors[BillStatus.SentToClient] : '#9E9E9E'
            const isSelected = selectedBillIds.includes(id) || (hasAdminPermissions && allSelected)

            return (
              <TableRow key={id} hover selected={isSelected}>
                {showCheckBox && (
                  <TableCell>
                    <Checkbox checked={isSelected} onChange={() => handleRowSelect(id)} size="small" />
                  </TableCell>
                )}

                <TableCell>
                  <Link
                    to={`/billing/bill-detail/${id}`}
                    state={{ isFinalBillView: true }}
                    style={{
                      color: palette.primary.main,
                      textDecoration: 'none'
                    }}
                  >
                    {id}
                  </Link>
                </TableCell>

                <TableCell>
                  {!!(caseNumber || consultMatterNumber) && (
                    <Link
                      to={`/matters/matter-detail/${caseData?.id}`}
                      style={{
                        color: palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      #{caseNumber || consultMatterNumber}
                    </Link>
                  )}
                </TableCell>

                <TableCell>
                  <Typography>{attorneyName}</Typography>
                </TableCell>

                <TableCell>
                  <Tooltip title={title || ''} arrow placement="top">
                    <Typography
                      noWrap
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        maxWidth: '350px'
                      }}
                    >
                      {title || ''}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Typography>{capitalizeCase(removeUnderScore(billing?.billingType?.toLowerCase() || ''))}</Typography>
                </TableCell>

                <TableCell>
                  <Typography>{issueDate ? format(new Date(issueDate), 'MM/dd/yyyy') : ''}</Typography>
                </TableCell>

                <TableCell>
                  <Typography>{formatCurrency(amount)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{formatCurrency(amountPaid)}</Typography>
                </TableCell>

                <TableCell>
                  <Typography>{formatCurrency(writtenOff)}</Typography>
                </TableCell>

                <TableCell>
                  <Typography>{formatCurrency(balance)}</Typography>
                </TableCell>

                {trustMode && (
                  <>
                    <TableCell>
                      <Typography>{formatCurrency(outstandingBalance || 0)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{hasTrust ? formatCurrency(trustBalance || 0) : '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{formatCurrency(creditBalance || 0)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{hasTrust ? formatCurrency(pullFromTrust) : '-'}</Typography>
                    </TableCell>
                  </>
                )}

                <TableCell>
                  <Chip
                    label={lastSentAt ? 'Yes' : 'No'}
                    size="small"
                    sx={{
                      color: sentColor,
                      border: `1px solid ${sentColor}50`,
                      backgroundColor: 'transparent'
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Typography>{lastSentAt ? format(new Date(lastSentAt), 'MM/dd/yyyy') : '-'}</Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={formattedStatus}
                    sx={{
                      color: statusColors[status],
                      border: `1px solid ${statusColors[status]}50`,
                      backgroundColor: 'transparent'
                    }}
                  />
                </TableCell>

                <TableCell align="right">
                  <Tooltip title="Print" arrow>
                    <IconButton aria-label="Print bill" onClick={() => handleRowPrint(Number(bill.id))}>
                      <PrintOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <BillsMenu
                    markDeliveredOnPrint
                    bill={bill}
                    caseId={caseData?.id}
                    billingType={caseData?.billing?.billingType}
                    billApprover={null}
                    isHourlyCase={billing ? isHourlyCase(billing) : false}
                    isContingencyCase={billing ? isContingencyCase(billing) : false}
                    onRefetch={refetch}
                    renderAction={handleClick => (
                      <IconButton onClick={handleClick}>
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}
